import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LockLocked20Px } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useTranslation } from "react-i18next";

import {
  settingsControlFontSize,
  settingsSelectSx
} from "~/features/settings/settings.styles";
import {
  useGetAutostoreGridQuery,
  useGetAutostoreGridsQuery
} from "~/redux/warehouse/autostoreGrid.hooks";

export function temperatureZoneToSelectValue(
  raw: string | undefined
): "" | "Ambient" | "Chilled" | "Frozen" {
  if (!raw) {
    return "";
  }
  const n = raw.toString().toLowerCase();
  if (n === "ambient") {
    return "Ambient";
  }
  if (n === "chilled") {
    return "Chilled";
  }
  if (n === "frozen") {
    return "Frozen";
  }
  return "";
}

type Props = {
  selectedGridId: string | undefined;
  onGridSelected: (gridId: string | undefined) => void | Promise<void>;
};

export function GridSection({ selectedGridId, onGridSelected }: Props) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { data: autostoreGrids } = useGetAutostoreGridsQuery();
  const handleGridChange = async (event: SelectChangeEvent) => {
    const nextGridId = event.target.value || undefined;
    await onGridSelected(nextGridId);
  };
  const { data: selectedAutostoreGrid } = useGetAutostoreGridQuery(
    selectedGridId && selectedGridId.length > 0 ? selectedGridId : skipToken
  );
  const tempZoneValue = temperatureZoneToSelectValue(
    selectedAutostoreGrid?.temperatureZones?.[0]
  );

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <FormControl fullWidth sx={{ maxWidth: 400 }}>
        <Select
          id="grid-selector-preview"
          value={selectedGridId ?? ""}
          onChange={handleGridChange}
          size="small"
          variant="outlined"
          displayEmpty
          sx={settingsSelectSx}
        >
          <MenuItem key="no-grid-selected" value="">
            {t("no autostore grid selected")}
          </MenuItem>
          {(autostoreGrids || []).map(
            ({ autostoreGridId, autostoreGridName }) => (
              <MenuItem key={autostoreGridId} value={autostoreGridId}>
                {autostoreGridName}
              </MenuItem>
            )
          )}
        </Select>
      </FormControl>
      <Stack
        direction="row"
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        role="group"
        aria-label={t("grid temp zone")}
      >
        <Typography
          variant="body2"
          color="textSecondary"
          alignSelf="center"
          sx={{ fontSize: settingsControlFontSize }}
        >
          {t("grid temp zone")}
        </Typography>
        <FormControl>
          <Select
            value={tempZoneValue}
            disabled
            variant="outlined"
            size="small"
            sx={settingsSelectSx}
          >
            <MenuItem value="Ambient">{t("ambient")}</MenuItem>
            <MenuItem value="Chilled">{t("chilled")}</MenuItem>
            <MenuItem value="Frozen">{t("frozen")}</MenuItem>
          </Select>
        </FormControl>
        <LockLocked20Px
          fill={palette.text.secondary}
          style={{ alignSelf: "center" }}
          aria-hidden
        />
      </Stack>
    </Stack>
  );
}
